import { useEffect, useMemo, useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { IconoLogistica, IconoCorreo, IconoReloj } from '../componentes/iconos'
import {
  useConfiguracion,
  useGuardarConfiguracion,
  type DefinicionAjuste,
} from '../hooks/useConfiguracion'

/** Agrupación visual (estilo C): cada ajuste conocido cae en una sección. */
const SECCIONES: { titulo: string; claves: string[] }[] = [
  { titulo: 'Reparto', claves: ['rutas_por_dia'] },
  { titulo: 'Correo de albaranes y rutas', claves: ['correo_rutas_hora', 'correo_rutas_destinatario', 'correo_rutas_cc'] },
  { titulo: 'Automatizaciones', claves: ['avisos_activos', 'recuperacion_max_semanas', 'ge_dias_antelacion_aviso'] },
]

/** Procesos programados del sistema (informativo, solo lectura). */
const HORARIOS_SISTEMA: { proceso: string; cuando: string }[] = [
  { proceso: 'Correo de rutas del repartidor', cuando: 'L–V · hora configurable arriba' },
  { proceso: 'Reconstrucción de avisos', cuando: 'Todos los días · 05:00' },
  { proceso: 'Albaranes de Grandes Empresas', cuando: 'Todos los días · 05:30' },
  { proceso: 'Consulta semanal del ciclo', cuando: 'Lunes · 09:00' },
  { proceso: 'Recordatorio del ciclo', cuando: 'Miércoles · 09:00' },
  { proceso: 'Corte del ciclo', cuando: 'Viernes · 16:00' },
  { proceso: 'Recálculo de etiquetas', cuando: 'Viernes · 17:00' },
]

function Stepper({ valor, min, max, onChange }: { valor: number; min?: number; max?: number; onChange: (n: number) => void }) {
  const dec = () => onChange(Math.max(min ?? 1, valor - 1))
  const inc = () => onChange(Math.min(max ?? 99, valor + 1))
  return (
    <div className="inline-flex items-center overflow-hidden rounded-lg border border-borde-fuerte">
      <button type="button" onClick={dec} className="h-9 w-10 bg-elevado text-lg text-tinta hover:brightness-95" aria-label="Menos">−</button>
      <span className="w-14 text-center text-base font-bold tabular-nums">{valor}</span>
      <button type="button" onClick={inc} className="h-9 w-10 bg-elevado text-lg text-tinta hover:brightness-95" aria-label="Más">+</button>
    </div>
  )
}

function ListaEmail({ valor, onChange }: { valor: string[]; onChange: (v: string[]) => void }) {
  const [borrador, setBorrador] = useState('')
  const añadir = () => {
    const e = borrador.trim()
    if (e && !valor.includes(e)) onChange([...valor, e])
    setBorrador('')
  }
  return (
    <div>
      <div className="mb-1 flex flex-wrap gap-1.5">
        {valor.length === 0 ? <span className="text-xs text-tinta-apagada">Sin copias.</span> : null}
        {valor.map((e) => (
          <span key={e} className="inline-flex items-center gap-1.5 rounded-full border border-acento-borde bg-acento-suave px-2.5 py-1 text-xs font-medium text-acento-tinta">
            {e}
            <button type="button" onClick={() => onChange(valor.filter((x) => x !== e))} aria-label={`Quitar ${e}`} className="opacity-60 hover:opacity-100">✕</button>
          </span>
        ))}
      </div>
      <input
        value={borrador}
        onChange={(ev) => setBorrador(ev.target.value)}
        onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); añadir() } }}
        onBlur={añadir}
        placeholder="añadir correo y pulsar Enter…"
        className="w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta"
      />
    </div>
  )
}

/** Un ajuste individual, renderizado según su tipo. */
function CampoAjuste({ def, valor, onChange }: { def: DefinicionAjuste; valor: unknown; onChange: (v: unknown) => void }) {
  switch (def.tipo) {
    case 'numero':
      return <Stepper valor={Number(valor ?? def.defecto)} min={def.min} max={def.max} onChange={onChange} />
    case 'lista_email':
      return <ListaEmail valor={Array.isArray(valor) ? (valor as string[]) : []} onChange={onChange} />
    case 'hora':
      return <input type="time" value={String(valor ?? '')} onChange={(e) => onChange(e.target.value)} className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta" />
    case 'booleano':
      return (
        <div className="inline-flex overflow-hidden rounded-lg border border-borde-fuerte">
          <button type="button" onClick={() => onChange(true)} className={`px-4 py-2 text-sm font-semibold ${valor ? 'bg-acento text-acento-contraste' : 'text-tinta'}`}>Activado</button>
          <button type="button" onClick={() => onChange(false)} className={`px-4 py-2 text-sm font-semibold ${!valor ? 'bg-acento text-acento-contraste' : 'text-tinta'}`}>Desactivado</button>
        </div>
      )
    default:
      return <input value={String(valor ?? '')} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta" />
  }
}

export function Configuracion() {
  const { data, isLoading, isError, error, refetch } = useConfiguracion()
  const guardar = useGuardarConfiguracion()
  const [borrador, setBorrador] = useState<Record<string, unknown>>({})

  // Cuando llegan los valores del backend, inicializa el borrador local.
  useEffect(() => {
    if (data?.valores) setBorrador({ ...data.valores })
  }, [data?.valores])

  const defs = useMemo(() => new Map((data?.definiciones ?? []).map((d) => [d.clave, d])), [data?.definiciones])
  const sucio = useMemo(
    () => data?.valores && JSON.stringify(borrador) !== JSON.stringify(data.valores),
    [borrador, data?.valores],
  )

  if (isLoading) return <SkeletonList rows={5} />
  if (isError) return <EstadoError error={error} onReintentar={() => refetch()} />

  const set = (clave: string, v: unknown) => setBorrador((b) => ({ ...b, [clave]: v }))

  const descripcionDe = (clave: string): string => {
    switch (clave) {
      case 'rutas_por_dia': return 'Nº de grupos de reparto que genera el sistema. 1 en temporada baja, más si crece la flota.'
      case 'correo_rutas_hora': return 'A qué hora (España) sale el correo diario de rutas. Se envía de lunes a viernes.'
      case 'correo_rutas_destinatario': return 'A quién llega el correo diario con la ruta y el PDF de albaranes.'
      case 'correo_rutas_cc': return 'Reciben una copia del mismo correo.'
      case 'avisos_activos': return 'Si está activado, el sistema envía los avisos semanales de reparto por WhatsApp. Desactívalo para pausarlos sin tocar nada más.'
      case 'recuperacion_max_semanas': return 'Máximo de semanas que un cliente puede llevar sin pedir para seguir avisándole. Pasado ese límite, queda fuera de los avisos.'
      case 'ge_dias_antelacion_aviso': return 'Para grandes empresas con recurrencia: con cuántos días de antelación avisa la campanita de que toca generar su pedido. 0 = el mismo día del reparto.'
      default: return ''
    }
  }

  return (
    <div className="max-w-2xl">
      {SECCIONES.map((sec) => {
        const ajustes = sec.claves.map((c) => defs.get(c)).filter(Boolean) as DefinicionAjuste[]
        if (ajustes.length === 0) return null
        return (
          <section key={sec.titulo} className="mb-7">
            <h3 className="mb-3 flex items-center gap-2 border-b border-borde pb-2 text-xs font-bold uppercase tracking-wider text-tinta-apagada">
              {sec.titulo === 'Reparto' ? <IconoLogistica className="size-4" /> : sec.titulo === 'Automatizaciones' ? <IconoReloj className="size-4" /> : <IconoCorreo className="size-4" />}
              {sec.titulo}
            </h3>
            <div className="space-y-5">
              {ajustes.map((def) => (
                <div key={def.clave}>
                  <label className="block text-sm font-semibold text-tinta">{def.etiqueta}</label>
                  <p className="mb-2 text-xs text-tinta-apagada">{descripcionDe(def.clave)}</p>
                  <CampoAjuste def={def} valor={borrador[def.clave]} onChange={(v) => set(def.clave, v)} />
                </div>
              ))}
            </div>
          </section>
        )
      })}

      <section className="mb-7">
        <h3 className="mb-3 flex items-center gap-2 border-b border-borde pb-2 text-xs font-bold uppercase tracking-wider text-tinta-apagada">
          <IconoReloj className="size-4" />
          Horarios del sistema
        </h3>
        <p className="mb-3 text-xs text-tinta-apagada">
          Cuándo corre cada proceso automático (hora de España). Informativo: la única hora editable es la del correo de rutas, arriba.
        </p>
        <ul className="divide-y divide-borde overflow-hidden rounded-lg border border-borde">
          {HORARIOS_SISTEMA.map((h) => (
            <li key={h.proceso} className="flex items-center justify-between bg-superficie px-3 py-2.5">
              <span className="text-sm text-tinta">{h.proceso}</span>
              <span className="text-xs font-medium tabular-nums text-tinta-apagada">{h.cuando}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="sticky bottom-0 flex justify-end gap-3 border-t border-borde bg-lienzo/80 py-3 backdrop-blur">
        {guardar.isError ? (
          <span className="mr-auto self-center text-sm text-negativo-tinta">
            {guardar.error instanceof Error ? guardar.error.message : 'No se pudo guardar.'}
          </span>
        ) : null}
        {guardar.isSuccess && !sucio ? (
          <span className="mr-auto self-center text-sm text-positivo-tinta">Guardado ✓</span>
        ) : null}
        <button
          type="button"
          disabled={!sucio}
          onClick={() => data?.valores && setBorrador({ ...data.valores })}
          className="rounded-lg border border-borde-fuerte px-4 py-2 text-sm font-semibold text-tinta-apagada disabled:opacity-40"
        >
          Descartar
        </button>
        <button
          type="button"
          disabled={!sucio || guardar.isPending}
          onClick={() => guardar.mutate(borrador)}
          className="rounded-lg bg-acento px-4 py-2 text-sm font-semibold text-acento-contraste hover:brightness-105 disabled:opacity-40"
        >
          {guardar.isPending ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  )
}
